import { FloatingPortal } from "@floating-ui/react";
import * as styles from "./ModalV3.styles.ts";
import {
  useEffect,
  type PropsWithChildren,
  createContext,
  type HTMLAttributes,
  type HTMLProps,
  useRef,
  useMemo,
  type MutableRefObject,
  type PointerEvent,
  Children,
} from "react";
import { CloseButton } from "src/components/CloseButton/CloseButton.tsx";
import { useModalV3Context } from "src/components/ModalV3/ModalV3.hooks.tsx";
import { LevelUp } from "src/components/LevelUp/LevevUp.tsx";
import { FLOATING_PORTAL_ROOT_ID } from "src/common/constants.ts";
import { H4 } from "src/components/Heading/Heading.tsx";

type ModalProps = PropsWithChildren<{
  onClose?(): void;
}>;

export const ModalV3Context = createContext<null | {
  onClose: () => void;
  isPointerDownInside: MutableRefObject<boolean>;
}>(null);

const ModalV3Impl = ({ children, onClose = () => {} }: ModalProps) => {
  const isPointerDownInside = useRef(false);
  useEffect(() => {
    const handleKeyDown = (ev: KeyboardEvent) =>
      ev.key === "Escape" && onClose?.();

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const context = useMemo(
    () => ({
      onClose,
      isPointerDownInside,
    }),
    [onClose],
  );

  return (
    <FloatingPortal id={FLOATING_PORTAL_ROOT_ID}>
      <ModalV3Context.Provider value={context}>
        {children}
      </ModalV3Context.Provider>
    </FloatingPortal>
  );
};

export const ModalContent = ({
  children,
  ...rest
}: PropsWithChildren<HTMLProps<HTMLDivElement>>) => {
  const { isPointerDownInside } = useModalV3Context();

  const handleModalContentPointerDown = () => {
    isPointerDownInside.current = true;
  };

  const handleModalContentPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    isPointerDownInside.current = false;
  };

  return (
    <div
      css={styles.container}
      onPointerDown={handleModalContentPointerDown}
      onPointerUp={handleModalContentPointerUp}
      onClick={(e) => {
        e.stopPropagation();
      }}
      {...rest}
    >
      {children}
    </div>
  );
};

export const ModalPositioner = ({
  children,
  positionX = "center",
  positionY = "center",
  ...rest
}: PropsWithChildren<
  HTMLProps<HTMLDivElement> & {
    positionX?: "center" | "right";
    positionY?: "center" | "bottom";
  }
>) => {
  const { onClose, isPointerDownInside } = useModalV3Context();

  const handleBackgroundPointerUp = () => {
    if (!isPointerDownInside.current) {
      onClose?.();
    }
    isPointerDownInside.current = false;
  };
  return (
    <div
      css={styles.root({ positionX, positionY })}
      onPointerUp={handleBackgroundPointerUp}
      {...rest}
    >
      <LevelUp type={"modals"}>{children}</LevelUp>
    </div>
  );
};

export const ModalFooter = ({
  children,
  ...rest
}: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) => {
  return (
    <div css={styles.footer} {...rest}>
      {children}
    </div>
  );
};

export const ModalButtonsContainer = ({
  children,
  ...rest
}: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) => {
  // Без пустых узлов: `{cond && <Button/>}` при false не должен занимать колонку.
  const count = Children.toArray(children).length;
  return (
    <div
      {...rest}
      css={styles.buttonsContainer({
        count,
      })}
    >
      {children}
    </div>
  );
};

export const ModalBody = ({
  children,
  ...rest
}: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) => {
  return (
    <div css={styles.body} {...rest}>
      {children}
    </div>
  );
};

const ModalHeader = ({
  children,
  ...rest
}: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) => {
  return (
    <div css={styles.header} {...rest}>
      {children}
    </div>
  );
};

const ModalTitle = ({
  children,
  ...rest
}: PropsWithChildren<HTMLAttributes<HTMLHeadingElement>>) => {
  return <H4 {...rest}>{children}</H4>;
};

export const CloseTrigger = ({
  position = "inside",
}: {
  position?: "inside" | "outside";
}) => {
  const { onClose } = useModalV3Context();
  if (position === "inside") {
    return <CloseButton css={styles.insideCloseButton} onClick={onClose} />;
  }
  return <CloseButton css={styles.outsideCloseButton} onClick={onClose} />;
};

export const ModalV3 = Object.assign(ModalV3Impl, {
  CloseTrigger: CloseTrigger,
  Content: ModalContent,
  Positioner: ModalPositioner,
  Footer: ModalFooter,
  FooterButtons: ModalButtonsContainer,
  Header: ModalHeader,
  Title: ModalTitle,
  Body: ModalBody,
});
